import React from 'react';
import PropTypes from 'prop-types';
import { FlexTableRow, PageWrapper, Paper } from 'virtues-bootstrap';
import { withTranslation } from 'react-i18next';
import { map } from 'ramda';

import { SUPER_TECHNICIAN, DOCTOR, SUPER_RESEARCHER } from 'constants/userRoles';
import { mapIndex } from 'utilities/ramda';

import PaginationSection from 'components/ui/PaginationSection';
import HeaderColumn from 'components/ui/HeaderColumn';
import PaperWithLoader from 'components/ui/PaperWithLoader';

import usePagination from 'hook/usePagination';
import { requestReportsList } from 'services/api/request/observation';
import { handleReportsReportsListMergedWithPatientData } from 'store/selectors/reports/recent';

import { PATIENT_ID, NAME } from './constant';
import HeaderSearch from './components/HeaderSearch';
import ReportsListRow from './components/Row';

import s from './styles.module.scss';

function ReportsList({ patientsData, userId, role, t }) {
  const withNotifications = role === DOCTOR;
  const performer = role === SUPER_RESEARCHER || role === SUPER_TECHNICIAN ? null : userId;

  const [{ data, initial, fetching, pagination, error, sorting }, getData] = usePagination({
    request: requestReportsList,
    params: { performer, sort: 'date', desc: true },
    selector: handleReportsReportsListMergedWithPatientData({ patientsData, withNotifications }),
  });

  const { total, page, count } = pagination;
  const { sort, desc } = sorting;
  const isAvailableName = role !== SUPER_TECHNICIAN;

  const handlePageChange = selectedPage => {
    getData({
      pagesOffset: count * selectedPage,
      page: selectedPage,
    });
  };

  const searchFields = [
    ...(isAvailableName ? [{ key: t('patient.name'), value: NAME }] : []),
    { key: t('patient.id'), value: PATIENT_ID },
  ];
  const headerColumns = [
    ...(isAvailableName ? [{ title: t('patient.patientName'), key: 'name' }] : []),
    { title: t('patient.patientId') },
    { title: t('patient.language') },
    { title: t('reports.assignedTo') },
    { title: t('dateAndTime'), sortKey: 'date' },
  ];

  return (
    <PageWrapper>
      <HeaderSearch onSubmit={getData} searchFields={searchFields} />
      <PaperWithLoader error={error} loading={initial || fetching}>
        <Paper className={s.mainPaper}>
          <FlexTableRow heading>
            {map(
              ({ title, sortKey }) => (
                <HeaderColumn
                  key={title}
                  sort={sort}
                  currentKey={sortKey}
                  title={t(title)}
                  desc={desc}
                  handleClick={getData}
                />
              ),
              headerColumns,
            )}
          </FlexTableRow>

          {mapIndex(
            (item, index) => (
              <ReportsListRow key={index} {...item} isAvailableName={isAvailableName} />
            ),
            data,
          )}
        </Paper>
        <PaginationSection
          activePage={page}
          disableInitialCallback
          onPageChange={handlePageChange}
          itemsPerPage={count}
          textKey="pagination.reports"
          total={total}
        />
      </PaperWithLoader>
    </PageWrapper>
  );
}

ReportsList.propTypes = {
  t: PropTypes.func.isRequired,
  patientsData: PropTypes.shape({}).isRequired,
  userId: PropTypes.string.isRequired,
  role: PropTypes.string.isRequired,
};

export default withTranslation()(ReportsList);
